import React from "react";
import { Link } from "react-router-dom";
import { Zap, ShieldCheck, ImageIcon, ArrowRight } from "lucide-react";
import Section from "../ui/Section";
import Glow from "../Glow";
import { usePageContent } from "../../lib/usePageContent";

// Icons cycle by position - the CMS (Admin > Homepage > Why Us Page)
// manages title/text per point, not an icon picker, same convention
// WhyChooseUs.jsx uses for its own admin-managed points.
const ICONS = [Zap, ShieldCheck, ImageIcon];

const DEFAULT_POINTS = [
  { title: "Drone-Powered Inspection", text: "Every solar clean includes a thermal drone pass, spotting issues a ladder crew would miss." },
  { title: "Safer, No Roof Access Needed", text: "Water-fed pole cleaning means less risk to your roof and no one walking on your panels." },
  { title: "Real Before & After Proof", text: "See actual results from real Sydney properties — not stock photos." },
];

/**
 * WhyUsPreview
 * --------------
 * Compact homepage teaser for the full /why-us page - the large,
 * point-by-point walkthrough that used to sit directly on the homepage
 * (WhyChooseUs.jsx) now lives there instead. Deliberately small: a
 * heading, 3 short points and one CTA, not another full section.
 */
export default function WhyUsPreview() {
  const page = usePageContent("why-us");
  const heading = page?.heading || "Why Choose Horizon?";
  const points = page?.body?.previewPoints?.length ? page.body.previewPoints : DEFAULT_POINTS;
  const ctaLabel = page?.ctaLabel || "See Why Horizon";

  return (
    <Section className="relative overflow-hidden bg-[#050910] py-20 border-t border-white/5">
      <Glow color="#22d3ee" className="w-[420px] h-[420px] -top-32 -left-32" opacity={0.06} />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-xl">
            <span className="text-[#22d3ee] tracking-widest text-sm font-semibold uppercase">Why Horizon</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white tracking-tight">{heading}</h2>
          </div>
          <Link
            to="/why-us"
            className="inline-flex items-center gap-2 flex-shrink-0 rounded-full px-6 py-3 border border-white/20 text-white font-semibold hover:border-white/50 transition-colors whitespace-nowrap"
          >
            {ctaLabel}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-3 gap-5">
          {points.slice(0, 3).map((p, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <div key={p.title || i} className="rounded-2xl bg-white/[0.03] border border-white/10 p-6">
                <Icon className="w-6 h-6 text-[#22d3ee]" />
                <h3 className="mt-4 font-bold text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
